import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { signOut } from '../lib/supabase';
import { B, FONT, LOGO_FALLBACK, fmtDate, today, WMO, WX_ICON } from '../lib/constants';
import { useGoogle } from '../hooks/useGoogle';
import AddTaskModal from './modals/AddTaskModal';
import AddPhotoModal from './modals/AddPhotoModal';
import { LogPickerModal } from './modals/LogPickerModal';
import AddDecisionModal from './modals/AddDecisionModal';
import DecisionDetailModal from './modals/DecisionDetailModal';
import DecisionTemplateLibrary from './DecisionTemplateLibrary';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { useFontSize } from '../hooks/useFontSize';


// Android back button closes the topmost open box instead of leaving the page
function useBackToClose(open, close) {
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ mhModal: true }, '');
    const onPop = () => close();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Closing via a button: neutralize the marker without navigating anywhere
      if (window.history.state && window.history.state.mhModal) window.history.replaceState({}, '');
    };
  }, [open]);
}

export default function Dashboard({ projects, onOpenProject, onRefresh, isAssistant = false }) {
  const [weather, setWeather] = useState({});
  const [calEvents, setCalEvents] = useState([]);
  const [showTaskPicker, setShowTaskPicker] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [showLogPicker, setShowLogPicker] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showNightlyLog, setShowNightlyLog] = useState(false);
  const { googleConnected, syncing, connectGoogle, syncCalendar, syncDecisions } = useGoogle();
  const { permission, requestPermission, sendTestNotification } = usePushNotifications();
  const { fontSize, setFontSize, SIZES } = useFontSize();
  const [showFontMenu, setShowFontMenu] = useState(false);

  useBackToClose(showTaskPicker, () => setShowTaskPicker(false));
  useBackToClose(showMediaPicker, () => setShowMediaPicker(false));
  useBackToClose(showLogPicker, () => setShowLogPicker(false));
  useBackToClose(showDecisionModal, () => setShowDecisionModal(false));
  useBackToClose(showTemplateLibrary, () => setShowTemplateLibrary(false));
  useBackToClose(showNightlyLog, () => setShowNightlyLog(false));
  useBackToClose(showFontMenu, () => setShowFontMenu(false));

  useEffect(() => {
    if (googleConnected && projects.length > 0) {
      syncCalendar(projects).then(() => loadCalEvents());
      syncDecisions(projects);
    }
  }, [googleConnected, projects.length]);

  useEffect(() => {
    if (!googleConnected) return;
    const interval = setInterval(() => {
      syncCalendar(projects).then(() => loadCalEvents());
      syncDecisions(projects);
    }, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [googleConnected, projects]);

  const loadCalEvents = async () => {
    const { data } = await supabase.from('calendar_events').select('*').order('event_date').order('event_time').limit(50);
    if (data) setCalEvents(data);
  };

  useEffect(() => {
    const COORDS = [
      { id: 1, lat: 35.9245, lng: -86.9028 },
      { id: 2, lat: 35.6151, lng: -86.9978 },
      { id: 3, lat: 35.8168, lng: -86.1372 },
      { id: 5, lat: 36.0986, lng: -86.8219 },
    ];
    COORDS.forEach(async ({ id, lat, lng }) => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
          `&current_weather=true&daily=temperature_2m_max,temperature_2m_min` +
          `&temperature_unit=fahrenheit&timezone=America%2FChicago&forecast_days=1`
        );
        const data = await res.json();
        const cw = data.current_weather;
        if (!cw) return;
        setWeather(prev => ({
          ...prev,
          [id]: {
            temp: Math.round(cw.temperature),
            icon: WX_ICON[cw.weathercode] || '🌤',
            condition: WMO[cw.weathercode] || 'Weather',
            hi: data.daily?.temperature_2m_max?.[0] ? Math.round(data.daily.temperature_2m_max[0]) : null,
            lo: data.daily?.temperature_2m_min?.[0] ? Math.round(data.daily.temperature_2m_min[0]) : null,
            forecastUrl: `https://forecast.weather.gov/MapClick.php?lat=${lat}&lon=${lng}`,
          }
        }));
      } catch(e) {}
    });
  }, []);

  useEffect(() => { loadCalEvents(); }, []);

  const localDate = (offset) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };
  const days = [0, 1, 2].map(localDate);
  const dayLabels = {
    [days[0]]: 'Today',
    [days[1]]: 'Tomorrow',
    [days[2]]: new Date(days[2] + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' }),
  };

  return (
    <div style={{ minHeight: '100vh', background: B.black, fontFamily: FONT }}>

      {/* Header */}
      <div style={{ background: B.black, padding: '18px 20px 14px', borderBottom: `1px solid ${B.slate}`, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowFontMenu(!showFontMenu)}
              style={{ background: 'transparent', border: `1px solid ${showFontMenu ? B.goldlt : B.slate}`, borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: showFontMenu ? B.goldlt : B.muted, fontSize: 14, lineHeight: 1 }}>
              Aa
            </button>
            {showFontMenu && (
              <div style={{ position: 'absolute', right: 0, top: 36, background: B.navy, border: `1px solid ${B.slate}`, borderRadius: 12, padding: 8, zIndex: 100, minWidth: 140, boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', padding: '4px 8px 8px' }}>Text Size</div>
                {SIZES.map(s => (
                  <button key={s.value} onClick={() => { setFontSize(s.value); setShowFontMenu(false); }}
                    style={{ width: '100%', background: fontSize === s.value ? 'rgba(154,115,47,0.15)' : 'transparent', border: 'none', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: FONT }}>
                    <span style={{ color: fontSize === s.value ? B.goldlt : B.light, fontSize: 13 }}>{s.label}</span>
                    {fontSize === s.value && <span style={{ color: B.goldlt, fontSize: 12 }}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <img src={LOGO_FALLBACK} alt="Muller Homes" style={{ height: 52, marginBottom: 8 }} onError={e => e.target.style.display = 'none'} />
        <div style={{ color: B.goldlt, fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', fontWeight: 700 }}>Muller Homes</div>
        <div style={{ color: B.muted, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginTop: 2 }}>Custom Builds, Exceptional Value</div>
        {!googleConnected && (
          <button onClick={connectGoogle}
            style={{ marginTop: 12, background: B.goldlt, color: B.black, border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 6 }}>
            🔗 Connect Google
          </button>
        )}
        {googleConnected && syncing && <div style={{ marginTop: 8, color: B.muted, fontSize: 11 }}>Syncing…</div>}
        {googleConnected && !syncing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <div style={{ color: '#4CAF7D', fontSize: 11 }}>✓ Google Connected</div>
            <button onClick={() => syncCalendar(projects).then(() => loadCalEvents())}
              style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 6, padding: '3px 8px', cursor: 'pointer', color: B.muted, fontSize: 10, fontFamily: FONT }}>
              ↻ Sync
            </button>
          </div>
        )}
      </div>

      {/* Project tabs */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(projects.length, 4)}, 1fr)`, gap: 6, padding: '12px 16px 0' }}>
        {projects.map(p => (
          <button key={p.id} onClick={() => onOpenProject(p.id)}
            style={{ background: B.navy, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '8px 4px', cursor: 'pointer', fontFamily: FONT, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <span style={{ color: B.white, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{p.name.split(' ')[0]}</span>
            <span style={{ color: B.goldlt, fontSize: 10, fontWeight: 800 }}>{p.progress}%</span>
          </button>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={{ padding: '12px 16px 0' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
          <button onClick={() => setShowMediaPicker(true)}
            style={{ background: B.navy, border: `1px solid ${B.goldlt}`, borderRadius: 10, padding: '10px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontFamily: FONT }}>
            <span style={{ fontSize: 16 }}>📷</span>
            <span style={{ color: B.goldlt, fontSize: 11, fontWeight: 700 }}>Photo</span>
          </button>
          <button onClick={() => setShowTaskPicker(true)}
            style={{ background: B.navy, border: `1px solid ${B.gold}`, borderRadius: 10, padding: '10px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontFamily: FONT }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <rect x="1" y="1" width="16" height="16" rx="3" stroke="#9A732F" strokeWidth="2"/>
              <path d="M4.5 9L7.5 12L13.5 6" stroke="#9A732F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ color: B.gold, fontSize: 11, fontWeight: 700 }}>Task</span>
          </button>
          <button onClick={() => setShowLogPicker(true)}
            style={{ background: B.navy, border: `1px solid ${B.muted}`, borderRadius: 10, padding: '10px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontFamily: FONT }}>
            <span style={{ fontSize: 16 }}>📋</span>
            <span style={{ color: B.light, fontSize: 11, fontWeight: 700 }}>Log</span>
          </button>
          <button onClick={() => setShowDecisionModal(true)}
            style={{ background: B.navy, border: `1px solid ${B.goldlt}`, borderRadius: 10, padding: '10px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontFamily: FONT }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M9 2L9 16" stroke="#F8DA6B" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M9 2L5 6" stroke="#F8DA6B" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M9 2L13 6" stroke="#F8DA6B" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M3 12C3 12 4.5 10 6 12C7.5 14 9 12 9 12" stroke="#F8DA6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              <path d="M9 12C9 12 10 10 12 12C14 14 15 12 15 12" stroke="#F8DA6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
            <span style={{ color: B.goldlt, fontSize: 11, fontWeight: 700 }}>Decision</span>
          </button>
        </div>
        <button onClick={() => setShowNightlyLog(true)}
          style={{ width: '100%', background: 'rgba(248,218,107,0.08)', border: `1px solid ${B.goldlt}`, borderRadius: 10, padding: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: FONT, marginBottom: 4 }}>
          <span style={{ fontSize: 16 }}>🎙</span>
          <span style={{ color: B.goldlt, fontSize: 12, fontWeight: 700 }}>Nightly Log — dictate all builds at once</span>
        </button>
        <HomeNotifications projects={projects} />
        <FocusList projects={projects} />
      </div>

      <div style={{ padding: '0 16px 24px' }}>

        {/* TASKS */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10 }}>Tasks</div>
          <GeneralTodos />
          {projects.map(p => <ProjectTodos key={p.id} project={p} onOpenProject={onOpenProject} />)}
        </div>

        {/* Active Builds */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10 }}>Active Builds</div>
          {projects.map(p => {
            const w = weather[parseInt(p.id)];
            return (
              <div key={p.id} onClick={() => onOpenProject(p.id)}
                style={{ background: B.navy, borderRadius: 16, padding: 18, marginBottom: 12, border: `1px solid ${B.slate}`, cursor: 'pointer', transition: 'border-color 0.15s' }}
                onTouchStart={e => e.currentTarget.style.borderColor = B.gold}
                onTouchEnd={e => e.currentTarget.style.borderColor = B.slate}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ color: B.white, fontSize: 16, fontWeight: 700 }}>{p.name}</div>
                    <div style={{ color: B.muted, fontSize: 11, marginTop: 2 }}>{p.address}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: B.goldlt, fontSize: 26, fontWeight: 900, lineHeight: 1 }}>{p.progress}%</div>
                    <div style={{ color: B.muted, fontSize: 10 }}>{p.phase}</div>
                  </div>
                </div>
                <div style={{ background: '#6F6F6F', borderRadius: 999, height: 5, overflow: 'hidden', marginBottom: 10 }}>
                  <div style={{ height: '100%', width: `${p.progress}%`, background: `linear-gradient(90deg,${B.goldlt},${B.gold})`, borderRadius: 999 }} />
                </div>
                {w && (
                  <a href={w.forecastUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                    style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, background: B.mid, borderRadius: 8, padding: '7px 10px' }}>
                    <span style={{ fontSize: 16 }}>{w.icon}</span>
                    <span style={{ color: B.white, fontSize: 12, fontWeight: 600 }}>{w.temp}°F · {w.condition}</span>
                    {w.hi && <span style={{ color: B.muted, fontSize: 11, marginLeft: 4 }}>H:{w.hi}° L:{w.lo}°</span>}
                    <span style={{ color: B.gold, fontSize: 9, fontWeight: 700, marginLeft: 'auto' }}>7-DAY →</span>
                  </a>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ color: B.muted, fontSize: 11 }}>{fmtDate(p.start_date)} → {fmtDate(p.est_complete)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Decisions */}
        <NeededDecisions projects={projects} onOpenProject={onOpenProject} />
        <MadeDecisions projects={projects} onOpenProject={onOpenProject} />

        {/* Schedule */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10 }}>Schedule</div>
          {days.slice(0, 3).map(day => {
            const parseTime = (t) => {
              if (!t || t === 'All Day') return -1;
              const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
              if (!m) return 0;
              let h = parseInt(m[1]), min = parseInt(m[2]), ampm = m[3].toUpperCase();
              if (ampm === 'PM' && h !== 12) h += 12;
              if (ampm === 'AM' && h === 12) h = 0;
              return h * 60 + min;
            };
            const events = calEvents.filter(e => e.event_date === day).sort((a, b) => parseTime(a.event_time) - parseTime(b.event_time));
            return (
              <div key={day} style={{ marginBottom: 8 }}>
                <div style={{ color: B.muted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{dayLabels[day]}</div>
                {events.length === 0 ? (
                  <div style={{ background: B.navy, borderRadius: 9, padding: '9px 14px', border: `1px solid ${B.slate}` }}>
                    <div style={{ color: B.muted, fontSize: 12 }}>Nothing scheduled</div>
                  </div>
                ) : events.map((ev, i) => (
                  <div key={i} style={{ background: B.navy, borderRadius: 9, padding: '9px 14px', marginBottom: 4, border: `1px solid ${B.slate}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ color: B.goldlt, fontSize: 11, fontWeight: 700, minWidth: 56, flexShrink: 0, borderRight: `1px solid ${B.gold}`, paddingRight: 10, marginRight: 4 }}>{ev.event_time}</div>
                    <div style={{ color: B.white, fontSize: 13, fontWeight: 600, flex: 1 }}>{ev.summary}</div>
                    {ev.calendar_name && ev.calendar_name !== 'Personal' && (
                      <div style={{ background: B.mid, color: B.muted, fontSize: 9, borderRadius: 5, padding: '2px 7px', flexShrink: 0 }}>{ev.calendar_name}</div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <button onClick={() => setShowTemplateLibrary(true)}
          style={{ width: '100%', background: 'transparent', border: `1px solid ${B.gold}`, borderRadius: 10, padding: '10px', cursor: 'pointer', color: B.goldlt, fontFamily: FONT, fontSize: 12, fontWeight: 700, marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <span>📚</span> Decision Library
        </button>
        <button onClick={() => signOut()}
          style={{ width: '100%', background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 10, padding: '10px', cursor: 'pointer', color: B.muted, fontFamily: FONT, fontSize: 12, marginTop: 8 }}>
          Sign Out
        </button>
      </div>

      {showTaskPicker && <AddTaskModal projects={projects} onClose={() => setShowTaskPicker(false)} onSuccess={onRefresh} />}
      {showMediaPicker && <AddPhotoModal projects={projects} onClose={() => setShowMediaPicker(false)} />}
      {showLogPicker && <LogPickerModal projects={projects} onClose={() => setShowLogPicker(false)} onOpenProject={(id) => { setShowLogPicker(false); onOpenProject(id, 'log'); }} />}
      {showDecisionModal && <AddDecisionModal projects={projects} onClose={() => setShowDecisionModal(false)} onSuccess={() => setShowDecisionModal(false)} />}
      {showTemplateLibrary && <DecisionTemplateLibrary projects={projects} onClose={() => setShowTemplateLibrary(false)} />}
      {showNightlyLog && <NightlyLogModal projects={projects} onClose={() => setShowNightlyLog(false)} />}
    </div>
  );
}

function GeneralTodos() {
  const [todos, setTodos] = useState([]);
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const { data } = await supabase.from('todos').select('*').is('project_id', null).eq('done', false).order('created_at', { ascending: false });
    if (data) setTodos(data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('todos-changed', h);
    return () => window.removeEventListener('todos-changed', h);
  }, []);

  const completeTodo = async (id) => {
    await supabase.from('todos').update({ done: true, done_at: new Date().toISOString() }).eq('id', id);
    setTodos(prev => prev.filter(t => t.id !== id));
    window.dispatchEvent(new Event('focuslist-changed'));
  };

  const addTodo = async () => {
    if (!newText.trim()) return;
    await supabase.from('todos').insert({ project_id: null, text: newText.trim(), priority: 'medium' });
    setNewText(''); setAdding(false); load();
  };

  return (
    <div style={{ background: B.navy, borderRadius: 12, padding: 14, marginBottom: 10, border: `1px solid ${B.gold}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ color: B.goldlt, fontSize: 13, fontWeight: 700 }}>General</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ color: B.muted, fontSize: 11 }}>{todos.length} open</div>
          <button onClick={() => setAdding(!adding)}
            style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>+</button>
        </div>
      </div>
      {adding && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <input autoFocus placeholder="New general task…" value={newText} onChange={e => setNewText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addTodo(); if (e.key === 'Escape') setAdding(false); }}
            style={{ flex: 1, background: B.mid, border: 'none', borderRadius: 7, padding: '8px 10px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT }} />
          <button onClick={addTodo} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 7, padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: FONT }}>Add</button>
        </div>
      )}
      {todos.length === 0 && !adding && <div style={{ color: B.muted, fontSize: 12 }}>No general tasks</div>}
      {todos.map(t => <TaskRow key={t.id} task={t} onComplete={() => completeTodo(t.id)} onUpdate={load} />)}
    </div>
  );
}

function ProjectTodos({ project, onOpenProject }) {
  const [todos, setTodos] = useState([]);
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const { data } = await supabase.from('todos').select('*').eq('project_id', project.id).eq('done', false).order('created_at', { ascending: false });
    if (data) setTodos(data);
  };

  useEffect(() => { load(); }, [project.id]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('todos-changed', h);
    return () => window.removeEventListener('todos-changed', h);
  }, []);

  const completeTodo = async (id) => {
    await supabase.from('todos').update({ done: true, done_at: new Date().toISOString() }).eq('id', id);
    // Sync: task created from a client checklist item completes that item too
    const t = todos.find(x => x.id === id);
    if (t?.source_checklist_id) {
      await supabase.from('checklist_items').update({
        done: true, done_by: 'builder', done_at: new Date().toISOString(),
      }).eq('id', t.source_checklist_id);
      try {
        const { notifyProjectClients } = await import('../hooks/usePushNotifications');
        await notifyProjectClients(project.id, '✓ Completed from your list', (t.text || '').slice(0, 80), window.location.origin);
      } catch(e) {}
    }
    setTodos(prev => prev.filter(t => t.id !== id));
    window.dispatchEvent(new Event('focuslist-changed'));
  };

  const addTodo = async () => {
    if (!newText.trim()) return;
    await supabase.from('todos').insert({ project_id: project.id, text: newText.trim(), priority: 'medium' });
    setNewText(''); setAdding(false); load();
  };

  return (
    <div style={{ background: B.navy, borderRadius: 12, padding: 14, marginBottom: 10, border: `1px solid ${B.slate}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ color: B.white, fontSize: 13, fontWeight: 700, cursor: 'pointer' }} onClick={() => onOpenProject(project.id, 'todos')}>{project.name}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ color: B.muted, fontSize: 11 }}>{todos.length} open</div>
          <button onClick={() => setAdding(!adding)} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>+</button>
        </div>
      </div>
      {adding && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          <input autoFocus placeholder="New task…" value={newText} onChange={e => setNewText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addTodo(); if (e.key === 'Escape') setAdding(false); }}
            style={{ flex: 1, background: B.mid, border: 'none', borderRadius: 7, padding: '8px 10px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT }} />
          <button onClick={addTodo} style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 7, padding: '8px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: FONT }}>Add</button>
        </div>
      )}
      {todos.length === 0 && !adding && <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', padding: '4px 0' }}>All caught up ✅</div>}
      {todos.map(t => <TaskRow key={t.id} task={t} onComplete={() => completeTodo(t.id)} onUpdate={load} />)}
    </div>
  );
}

function MadeDecisions({ projects, onOpenProject }) {
  const [decisions, setDecisions] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dismissed_decisions') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    loadDecisions();
    const handler = (e) => {
      if (e.data?.type === 'DECISION_NOTED' && e.data?.decisionId) {
        const id = String(e.data.decisionId);
        setDismissed(prev => {
          const updated = [...new Set([...prev, id])];
          localStorage.setItem('dismissed_decisions', JSON.stringify(updated));
          return updated;
        });
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, []);

  const loadDecisions = async () => {
    const { data } = await supabase.from('decisions').select('*').eq('status', 'approved').order('completed_at', { ascending: false }).limit(20);
    if (data) {
      setDecisions(data);
      const today = new Date().toDateString();
      const lastClear = localStorage.getItem('decisions_cleared_date');
      if (lastClear !== today) {
        const existingIds = JSON.parse(localStorage.getItem('dismissed_decisions') || '[]');
        const allIds = [...new Set([...existingIds, ...data.map(d => d.id)])];
        localStorage.setItem('dismissed_decisions', JSON.stringify(allIds));
        localStorage.setItem('decisions_cleared_date', today);
        setDismissed(allIds);
      }
    }
  };

  const dismiss = (id) => {
    const updated = [...dismissed, id];
    setDismissed(updated);
    localStorage.setItem('dismissed_decisions', JSON.stringify(updated));
  };

  const visible = decisions.filter(d => !dismissed.includes(d.id));
  if (visible.length === 0) return null;

  const getProjectName = (pid) => projects.find(p => parseInt(p.id) === parseInt(pid))?.name?.split(' ')[0] || 'Build';

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10 }}>Made Decisions</div>
      {visible.map(d => (
        <div key={d.id} style={{ background: B.navy, borderRadius: 12, padding: '12px 14px', marginBottom: 8, border: '1px solid #1A1A1A', borderLeft: '3px solid #4CAF7D', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onOpenProject(d.project_id, 'decisions')}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
              <div style={{ color: B.muted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>{getProjectName(d.project_id)}</div>
              {d.category && <div style={{ color: B.gold, fontSize: 10 }}>· {d.category}</div>}
            </div>
            <div style={{ color: B.white, fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{d.item}</div>
            <div style={{ color: '#4CAF7D', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.answer}</div>
            <div style={{ color: B.muted, fontSize: 10, marginTop: 3, textTransform: 'capitalize' }}>
              {d.completed_by || 'Builder'} · {d.completed_at ? new Date(d.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
            </div>
          </div>
          <button onClick={() => dismiss(d.id)}
            style={{ background: 'transparent', border: `2px solid ${B.slate}`, borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>
            <span style={{ color: B.muted, fontSize: 10, lineHeight: 1 }}>✓</span>
          </button>
        </div>
      ))}
    </div>
  );
}

function NeededDecisions({ projects, onOpenProject }) {
  const [decisions, setDecisions] = useState([]);
  const [selectedDecision, setSelectedDecision] = useState(null);
  useBackToClose(!!selectedDecision, () => setSelectedDecision(null));

  const load = () => {
    supabase.from('decisions').select('*').in('status', ['pending', 'pending_confirmation'])
      .order('due_date', { ascending: true }).limit(20)
      .then(({ data }) => { if (data) setDecisions(data); });
  };

  useEffect(() => { load(); }, []);

  if (decisions.length === 0) return null;

  const getPriority = (due_date) => {
    if (!due_date) return null;
    const today = new Date(); today.setHours(0,0,0,0);
    const days = Math.round((new Date(due_date + 'T12:00:00') - today) / 86400000);
    if (days < 0) return { label: 'Past Due', color: '#E74C3C' };
    if (days <= 2) return { label: 'Urgent', color: '#E67E22' };
    if (days <= 7) return { label: 'This Week', color: '#F8DA6B' };
    return null;
  };

  const byProject = projects.map(p => ({
    project: p,
    decisions: decisions.filter(d => parseInt(d.project_id) === parseInt(p.id)),
  })).filter(g => g.decisions.length > 0);

  if (byProject.length === 0) return null;

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 10 }}>
        Decisions · {decisions.length}
      </div>
      {byProject.map(({ project: p, decisions: pDecs }) => (
        <div key={p.id} style={{ marginBottom: 14 }}>
          <div style={{ color: B.white, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, paddingBottom: 4, borderBottom: `1px solid ${B.slate}` }}>
            {p.name.split(' ')[0]} · {pDecs.length}
          </div>
          {pDecs.map(d => {
            const priority = getPriority(d.due_date);
            return (
              <div key={d.id} onClick={() => setSelectedDecision(d)}
                style={{ background: B.navy, borderRadius: 12, padding: '12px 14px', marginBottom: 6, border: `1px solid ${priority?.color || B.slate}`, borderLeft: `3px solid ${priority?.color || B.gold}`, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: B.white, fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.item}</div>
                    {d.category && <div style={{ color: B.gold, fontSize: 10, marginTop: 1 }}>{d.category}</div>}
                  </div>
                  <div style={{ flexShrink: 0, marginLeft: 10, textAlign: 'right' }}>
                    {d.status === 'pending_confirmation' && <div style={{ color: '#4FA3E0', fontSize: 10, fontWeight: 700 }}>Awaiting ✓</div>}
                    {priority && <div style={{ color: priority.color, fontSize: 10, fontWeight: 700 }}>{priority.label}</div>}
                    {d.due_date && <div style={{ color: B.muted, fontSize: 10 }}>{new Date(d.due_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {selectedDecision && (
        <DecisionDetailModal
          decision={selectedDecision}
          isBuilder={true}
          onClose={() => setSelectedDecision(null)}
          onSuccess={() => { setSelectedDecision(null); load(); }}
        />
      )}
    </div>
  );
}

function TaskRow({ task: t, onComplete, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(t.text);
  const [priority, setPriority] = useState(t.priority || 'medium');
  const [saving, setSaving] = useState(false);
  const [description, setDescription] = useState(t.description || '');
  const [link, setLink] = useState(t.link || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [fullPhoto, setFullPhoto] = useState(null);
  useBackToClose(editing, () => setEditing(false));
  useBackToClose(!!fullPhoto, () => setFullPhoto(null));

  const handleSave = async () => {
    setSaving(true);
    let photo_url = t.photo_url || null;
    if (photoFile) {
      try {
        const path = `tasks/${t.id}_${Date.now()}_${photoFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const { error: upErr } = await supabase.storage.from('purchase-photos').upload(path, photoFile);
        if (!upErr) {
          const { data } = supabase.storage.from('purchase-photos').getPublicUrl(path);
          photo_url = data.publicUrl;
        }
      } catch(e) { console.error('Task photo upload failed:', e); }
    }
    await supabase.from('todos').update({ text: text.trim(), priority, description: description.trim() || null, link: link.trim() || null, photo_url }).eq('id', t.id);
    setSaving(false); setEditing(false); onUpdate();
  };

  const handleDelete = async () => {
    await supabase.from('todos').delete().eq('id', t.id);
    onUpdate();
  };

  if (editing) return (
    <div style={{ background: B.mid, borderRadius: 10, padding: 12, marginBottom: 8, border: `1px solid ${B.gold}` }}>
      <textarea value={text} onChange={e => setText(e.target.value)} rows={2} autoFocus
        style={{ width: '100%', background: B.navy, border: 'none', borderRadius: 7, padding: '8px 10px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'none', marginBottom: 8 }} />
      <input value={link} onChange={e => setLink(e.target.value)} placeholder="🔗 Link (optional)"
        style={{ width: '100%', background: B.navy, border: 'none', borderRadius: 8, padding: '9px 12px', color: B.white, fontSize: 12, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', marginBottom: 8 }} />
      <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="📝 Description / details (optional)"
        style={{ width: '100%', background: B.navy, border: 'none', borderRadius: 8, padding: '9px 12px', color: B.white, fontSize: 12, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'vertical', marginBottom: 8 }} />
      {(photoPreview || t.photo_url) && (
        <img src={photoPreview || t.photo_url} alt=""
          onClick={() => setFullPhoto(photoPreview || t.photo_url)}
          style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 9, marginBottom: 8, border: `1px solid ${B.slate}`, cursor: 'zoom-in' }} />
      )}
      <label style={{ display: 'block', border: `1px dashed ${B.slate}`, borderRadius: 9, padding: '8px 12px', cursor: 'pointer', color: B.muted, fontSize: 11, textAlign: 'center', marginBottom: 10 }}>
        📷 {t.photo_url || photoPreview ? 'Replace photo' : 'Add photo (optional)'}
        <input type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file)); }
          }} />
      </label>
      {fullPhoto && (
        <div onClick={() => setFullPhoto(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
          <img src={fullPhoto} alt="" style={{ maxWidth: '100vw', maxHeight: '100vh', objectFit: 'contain' }} />
          <button onClick={() => setFullPhoto(null)}
            style={{ position: 'fixed', top: 18, right: 18, background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {['low', 'medium', 'high'].map(p => (
          <button key={p} onClick={() => setPriority(p)}
            style={{ flex: 1, background: priority === p ? B.goldlt : B.slate, color: priority === p ? B.black : B.muted, border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONT, textTransform: 'capitalize' }}>
            {p}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={handleSave} disabled={saving}
          style={{ flex: 1, background: B.goldlt, color: B.black, border: 'none', borderRadius: 7, padding: '8px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
          {saving ? '…' : 'Save'}
        </button>
        <button onClick={() => setEditing(false)}
          style={{ flex: 1, background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 7, padding: '8px', cursor: 'pointer', color: B.muted, fontSize: 12, fontFamily: FONT }}>
          Cancel
        </button>
        <button onClick={handleDelete}
          style={{ background: 'transparent', border: '1px solid #E74C3C', borderRadius: 7, padding: '8px 10px', cursor: 'pointer', color: '#E74C3C', fontSize: 12, fontFamily: FONT }}>
          Delete
        </button>
      </div>
    </div>
  );

  const toggleFocus = async (e) => {
    e.stopPropagation();
    if (t.focus) {
      await supabase.from('todos').update({ focus: false }).eq('id', t.id);
    } else {
      const { data: maxRow } = await supabase.from('todos').select('focus_sort').eq('focus', true).order('focus_sort', { ascending: false }).limit(1);
      const nextSort = ((maxRow && maxRow[0]?.focus_sort) || 0) + 1;
      await supabase.from('todos').update({ focus: true, focus_sort: nextSort }).eq('id', t.id);
    }
    onUpdate();
    window.dispatchEvent(new Event('focuslist-changed'));
  };

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${B.slate}` }}>
      <button onClick={e => { e.stopPropagation(); onComplete(); }}
        style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${B.gold}`, background: 'transparent', cursor: 'pointer', flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setEditing(true)}>
        <div style={{ color: B.light, fontSize: 13, lineHeight: 1.4 }}>{t.text}
          {(t.description || t.link || t.photo_url) && (
            <span style={{ color: '#6F6F6F', fontSize: 10, marginLeft: 6 }}>
              {t.photo_url ? '📷' : ''}{t.link ? '🔗' : ''}{t.description ? '📝' : ''}
            </span>
          )}
        </div>
        {t.priority && <div style={{ color: t.priority === 'high' ? B.goldlt : B.muted, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', marginTop: 2 }}>{t.priority}</div>}
      </div>
      <button onClick={toggleFocus} title={t.focus ? 'On your To-Do list — tap to remove' : 'Add to your To-Do list'}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, padding: '0 2px', flexShrink: 0, opacity: t.focus ? 1 : 0.35 }}>
        📌
      </button>
    </div>
  );
}


const ALL_TRADES = ['Framing', 'Electrical', 'Plumbing', 'HVAC', 'Concrete', 'Roofing', 'Drywall', 'Paint', 'Tile', 'Flooring', 'Cabinets', 'Trim', 'Insulation', 'Excavation', 'Masonry', 'Gutters', 'Windows/Doors', 'Landscaping', 'Inspection', 'Cleaning'];

function NightlyLogModal({ projects, onClose }) {
  const [step, setStep] = useState('dictate'); // 'dictate' | 'review' | 'done'
  const [text, setText] = useState('');
  const [entries, setEntries] = useState([]);
  const [logDay, setLogDay] = useState('today');
  const [saving, setSaving] = useState(false);

  const localDateStr = (offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  };

  // Split the dictated text into per-project chunks using project names as markers
  const parseText = () => {
    let src = text.trim();
    if (!src) return;
    // "yesterday" anywhere in the dictation dates the whole log to yesterday
    if (/\byesterday\b/i.test(src)) {
      setLogDay('yesterday');
      src = src.replace(/\byesterday\b[\s,.:]*/gi, ' ').replace(/\s{2,}/g, ' ').trim();
    } else {
      setLogDay('today');
    }
    // Build markers from project names + common speech-to-text misspellings
    const ALIASES = {
      chassey: ['chassey', 'chassis', 'chassy', 'chasse', 'chasey', 'chassie', 'jesse', 'jessie', 'jessy', 'chessy', 'chesse'],
      mcvey: ['mcvey', 'mcveigh', 'mcvay', 'mick vey', 'mac vey', 'mick vay', 'mickvey'],
      landers: ['landers', 'lander', 'landor', 'launders'],
      redbud: ['redbud', 'red bud', 'redbird', 'red bird'],
    };
    const markers = [];
    projects.forEach(p => {
      const first = p.name.split(' ')[0].toLowerCase();
      const tokens = ALIASES[first] || [first];
      tokens.forEach(token => markers.push({ project: p, token }));
    });
    // Find all marker hit positions
    const lower = src.toLowerCase();
    const hits = [];
    markers.forEach(m => {
      let idx = lower.indexOf(m.token);
      while (idx !== -1) {
        hits.push({ pos: idx, project: m.project, len: m.token.length });
        idx = lower.indexOf(m.token, idx + 1);
      }
    });
    hits.sort((a, b) => a.pos - b.pos);
    // Keep first hit per project only
    const seen = new Set();
    const firstHits = hits.filter(h => {
      if (seen.has(h.project.id)) return false;
      seen.add(h.project.id);
      return true;
    });
    if (firstHits.length === 0) {
      setEntries([{ project: null, notes: src, trades: [] }]);
      setStep('review');
      return;
    }
    const isNothing = (s) => /^(nothing|nothing today|nothing new|no update|no updates|nothing happened|nada|n\/a)[\s.,!]*$/i.test(s.trim());
    const TRADE_KEYWORDS = {
      'Framing': ['framing', 'framer', 'framers', 'frame crew'],
      'Electrical': ['electric', 'electrician', 'electricians', 'wire', 'wiring', 'low voltage'],
      'Plumbing': ['plumb', 'plumber', 'plumbers'],
      'HVAC': ['hvac', 'heat and air', 'ductwork', 'ducts', 'furnace', 'ac guys'],
      'Concrete': ['concrete', 'footings', 'slab', 'flatwork', 'pour'],
      'Roofing': ['roof', 'roofer', 'roofers', 'shingle'],
      'Drywall': ['drywall', 'sheetrock', 'hang and finish', 'mud and tape'],
      'Paint': ['paint', 'painter', 'painters', 'stain'],
      'Tile': ['tile', 'tiler', 'tile guys'],
      'Flooring': ['flooring', 'hardwood', 'floor guys', 'lvp', 'carpet'],
      'Cabinets': ['cabinet', 'cabinets', 'built-ins', 'built ins'],
      'Trim': ['trim', 'trim guys', 'millwork', 'baseboard', 'crown'],
      'Insulation': ['insulation', 'insulate', 'spray foam'],
      'Excavation': ['excavat', 'grading', 'dirt work', 'gravel', 'site work'],
      'Masonry': ['brick', 'mason', 'stone', 'stucco'],
      'Gutters': ['gutter', 'gutters'],
      'Windows/Doors': ['windows', 'window install', 'doors', 'door handles', 'garage door'],
      'Landscaping': ['landscap', 'sod', 'irrigation'],
      'Inspection': ['inspect', 'inspection', 'inspector'],
      'Cleaning': ['clean', 'cleaners', 'punch'],
    };
    const detectTrades = (s) => {
      const low = s.toLowerCase();
      return Object.entries(TRADE_KEYWORDS)
        .filter(([, kws]) => kws.some(k => low.includes(k)))
        .map(([trade]) => trade);
    };
    const out = firstHits.map((h, i) => {
      const start = h.pos + h.len;
      const end = i + 1 < firstHits.length ? firstHits[i + 1].pos : src.length;
      let chunk = src.slice(start, end).trim();
      chunk = chunk.replace(/^[\s,:.\u2014-]+/, '').trim();
      return { project: h.project, notes: chunk, trades: detectTrades(chunk) };
    }).filter(e => e.notes.length > 0 && !isNothing(e.notes));
    // Anything before the first marker becomes an unassigned note
    const preamble = src.slice(0, firstHits[0].pos).trim();
    if (preamble.length > 3) out.unshift({ project: null, notes: preamble, trades: [] });
    setEntries(out);
    setStep('review');
  };

  const updateEntry = (i, field, value) => {
    setEntries(prev => prev.map((e, idx) => idx === i ? { ...e, [field]: value } : e));
  };

  const removeEntry = (i) => setEntries(prev => prev.filter((_, idx) => idx !== i));

  const saveAll = async () => {
    const valid = entries.filter(e => e.project && e.notes.trim());
    if (valid.length === 0) return;
    setSaving(true);
    for (const e of valid) {
      await supabase.from('daily_logs').insert({
        project_id: parseInt(e.project.id),
        log_date: localDateStr(logDay === 'yesterday' ? -1 : 0),
        notes: e.notes.trim(),
        trades: e.trades && e.trades.length ? e.trades : null,
      });
    }
    setSaving(false);
    setStep('done');
    setTimeout(onClose, 1500);
  };

  const inputStyle = { width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 14, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', lineHeight: 1.6, resize: 'vertical' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '20px 16px' }}>
      <div style={{ background: B.navy, borderRadius: 18, padding: 22, width: '100%', maxWidth: 560 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ color: B.goldlt, fontSize: 16, fontWeight: 800 }}>🎙 Nightly Log</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: B.muted, fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {step === 'dictate' && (
          <>
            <div style={{ color: B.muted, fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>
              Tap the box, hit the <b style={{ color: B.light }}>mic on your keyboard</b>, and talk through every build.
              Say the build name before each update — e.g. <span style={{ color: B.goldlt }}>"McVey — framing passed inspection. Chassey — tile started upstairs."</span>
            </div>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={8} autoFocus
              placeholder="McVey — … Chassey — … Landers — … Redbud — …"
              style={{ ...inputStyle, marginBottom: 14 }} />
            <button onClick={parseText} disabled={!text.trim()}
              style={{ width: '100%', background: text.trim() ? B.goldlt : B.slate, color: text.trim() ? B.black : B.muted, border: 'none', borderRadius: 12, padding: 14, cursor: text.trim() ? 'pointer' : 'default', fontSize: 14, fontWeight: 800, fontFamily: FONT }}>
              Split Into Entries →
            </button>
          </>
        )}

        {step === 'review' && (
          <>
            <div style={{ color: B.muted, fontSize: 12, marginBottom: 10 }}>Review each entry. Fix anything the mic mangled, assign any unmatched text, then save.</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>Log date:</div>
              {['today', 'yesterday'].map(d => (
                <button key={d} onClick={() => setLogDay(d)}
                  style={{ background: logDay === d ? B.goldlt : 'transparent', color: logDay === d ? B.black : B.muted, border: `1px solid ${logDay === d ? B.goldlt : B.slate}`, borderRadius: 999, padding: '5px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 700, fontFamily: FONT, textTransform: 'capitalize' }}>
                  {d} · {localDateStr(d === 'yesterday' ? -1 : 0).slice(5).replace('-', '/')}
                </button>
              ))}
            </div>
            {entries.map((e, i) => (
              <div key={i} style={{ background: B.mid, borderRadius: 12, padding: 14, marginBottom: 10, border: `1px solid ${e.project ? B.slate : '#E74C3C'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <select value={e.project?.id || ''} onChange={ev => updateEntry(i, 'project', projects.find(p => String(p.id) === ev.target.value) || null)}
                    style={{ background: B.navy, border: `1px solid ${e.project ? B.gold : '#E74C3C'}`, borderRadius: 8, padding: '6px 10px', color: e.project ? B.goldlt : '#E74C3C', fontSize: 12, fontWeight: 700, outline: 'none', fontFamily: FONT }}>
                    <option value="">Assign build…</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <button onClick={() => removeEntry(i)} style={{ background: 'none', border: 'none', color: '#555', fontSize: 16, cursor: 'pointer' }}>×</button>
                </div>
                <textarea value={e.notes} onChange={ev => updateEntry(i, 'notes', ev.target.value)} rows={3} style={{ ...inputStyle, marginBottom: 10 }} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ALL_TRADES.map(tr => {
                    const on = (e.trades || []).includes(tr);
                    return (
                      <button key={tr}
                        onClick={() => updateEntry(i, 'trades', on ? e.trades.filter(t => t !== tr) : [...(e.trades || []), tr])}
                        style={{ background: on ? B.goldlt : 'transparent', color: on ? B.black : B.muted, border: `1px solid ${on ? B.goldlt : B.slate}`, borderRadius: 999, padding: '4px 11px', cursor: 'pointer', fontSize: 10.5, fontWeight: on ? 800 : 500, fontFamily: FONT }}>
                        {tr}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button onClick={() => setStep('dictate')}
                style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 12, padding: '13px 18px', cursor: 'pointer', color: B.muted, fontSize: 13, fontFamily: FONT }}>
                ← Back
              </button>
              <button onClick={saveAll} disabled={saving || entries.filter(e => e.project && e.notes.trim()).length === 0}
                style={{ flex: 1, background: B.goldlt, color: B.black, border: 'none', borderRadius: 12, padding: 13, cursor: 'pointer', fontSize: 14, fontWeight: 800, fontFamily: FONT }}>
                {saving ? 'Saving…' : `Save ${entries.filter(e => e.project && e.notes.trim()).length} Log ${entries.filter(e => e.project && e.notes.trim()).length === 1 ? 'Entry' : 'Entries'}`}
              </button>
            </div>
          </>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>✅</div>
            <div style={{ color: B.white, fontSize: 16, fontWeight: 700 }}>Logs saved</div>
          </div>
        )}
      </div>
    </div>
  );
}


function HomeNotifications({ projects }) {
  const [items, setItems] = useState([]);

  const load = async () => {
    const { data } = await supabase.from('app_notifications').select('*')
      .eq('recipient_role', 'builder').eq('read', false)
      .order('created_at', { ascending: false }).limit(50);
    if (data) setItems(data);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await supabase.from('app_notifications').update({ read: true }).eq('id', id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const markAllRead = async () => {
    await supabase.from('app_notifications').update({ read: true }).eq('recipient_role', 'builder').eq('read', false);
    setItems([]);
  };

  const projectName = (pid) => projects.find(p => parseInt(p.id) === parseInt(pid))?.name?.split(' ')[0] || '';

  if (items.length === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase' }}>🔔 Notifications · {items.length}</div>
        {items.length > 1 && (
          <button onClick={markAllRead}
            style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 7, padding: '4px 10px', cursor: 'pointer', color: B.muted, fontSize: 10, fontWeight: 700, fontFamily: FONT }}>
            ✓ Mark all read
          </button>
        )}
      </div>
      {items.map(n => (
        <div key={n.id} style={{ background: B.navy, borderRadius: 11, padding: '11px 13px', marginBottom: 6, border: `1px solid ${B.slate}`, borderLeft: `3px solid ${B.goldlt}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0, cursor: n.url ? 'pointer' : 'default' }}
            onClick={() => { if (n.url) { markRead(n.id); setTimeout(() => { window.location.href = n.url; }, 120); } }}>
            {projectName(n.project_id) && <div style={{ color: B.muted, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>{projectName(n.project_id)}</div>}
            <div style={{ color: B.white, fontSize: 12.5, fontWeight: 700 }}>{n.title}</div>
            {n.body && <div style={{ color: B.light, fontSize: 11.5, lineHeight: 1.45 }}>{n.body}</div>}
            <div style={{ color: '#555', fontSize: 9.5, marginTop: 3 }}>
              {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(n.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </div>
          </div>
          <button onClick={() => markRead(n.id)}
            style={{ background: 'rgba(76,175,61,0.1)', border: '1px solid #4CAF7D', borderRadius: 8, padding: '6px 11px', cursor: 'pointer', color: '#4CAF7D', fontSize: 10.5, fontWeight: 700, fontFamily: FONT, flexShrink: 0 }}>
            ✓ Read
          </button>
        </div>
      ))}
    </div>
  );
}


function FocusList({ projects }) {
  const [items, setItems] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [order, setOrder] = useState([]);
  const rowRefs = useRef({});

  const load = async () => {
    const { data } = await supabase.from('todos').select('*')
      .eq('focus', true).eq('done', false)
      .order('focus_sort', { ascending: true });
    if (data) { setItems(data); setOrder(data); }
  };
  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener('focuslist-changed', h);
    window.addEventListener('todos-changed', h);
    return () => { window.removeEventListener('focuslist-changed', h); window.removeEventListener('todos-changed', h); };
  }, []);

  useEffect(() => {
    if (!dragId) return;
    const prevent = (e) => e.preventDefault();
    document.addEventListener('touchmove', prevent, { passive: false });
    return () => document.removeEventListener('touchmove', prevent);
  }, [dragId]);

  const dragStart = (id) => (e) => {
    e.stopPropagation();
    setDragId(id);
    if (navigator.vibrate) navigator.vibrate(15);
  };
  const dragMove = (e) => {
    if (!dragId) return;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const cur = order.findIndex(t => t.id === dragId);
    if (cur < 0) return;
    let target = cur;
    for (let i = 0; i < order.length; i++) {
      if (i === cur) continue;
      const el = rowRefs.current[order[i].id];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      if (i < cur && y < midY) { target = i; break; }
      if (i > cur && y > midY) target = i;
    }
    if (target !== cur) {
      const n = [...order];
      const [moved] = n.splice(cur, 1);
      n.splice(target, 0, moved);
      setOrder(n);
    }
  };
  const dragEnd = async () => {
    if (dragId) {
      const changed = order.some((t, i) => t.id !== items[i]?.id);
      if (changed) {
        for (let i = 0; i < order.length; i++) {
          await supabase.from('todos').update({ focus_sort: i + 1 }).eq('id', order[i].id);
        }
        load();
      }
    }
    setDragId(null);
  };

  const complete = async (t) => {
    await supabase.from('todos').update({ done: true, done_at: new Date().toISOString() }).eq('id', t.id);
    load();
    window.dispatchEvent(new Event('todos-changed'));
  };

  const unpin = async (t) => {
    await supabase.from('todos').update({ focus: false }).eq('id', t.id);
    load();
  };

  const projectName = (pid) => {
    if (!pid) return 'General';
    return projects.find(p => parseInt(p.id) === parseInt(pid))?.name?.split(' ')[0] || '';
  };

  if (order.length === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 8 }}>📌 My To-Do List · {order.length}</div>
      <div
        onTouchMove={dragMove} onTouchEnd={dragEnd} onTouchCancel={dragEnd}
        onMouseMove={dragId ? dragMove : undefined} onMouseUp={dragId ? dragEnd : undefined} onMouseLeave={dragId ? dragEnd : undefined}>
        {order.map(t => (
          <div key={t.id} ref={el => rowRefs.current[t.id] = el}
            style={{ background: B.navy, borderRadius: 11, padding: '11px 13px', marginBottom: 6, border: `1px solid ${dragId === t.id ? B.goldlt : B.slate}`, borderLeft: `3px solid ${B.goldlt}`, display: 'flex', alignItems: 'center', gap: 10, opacity: dragId === t.id ? 0.5 : 1 }}>
            <div onTouchStart={dragStart(t.id)} onMouseDown={dragStart(t.id)}
              style={{ color: B.muted, fontSize: 15, cursor: 'grab', padding: '2px 1px', touchAction: 'none', userSelect: 'none' }}>⠿</div>
            <button onClick={() => complete(t)}
              style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${B.gold}`, background: 'transparent', cursor: 'pointer', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: B.white, fontSize: 12.5, fontWeight: 600, lineHeight: 1.35 }}>{t.text}</div>
              <div style={{ color: B.muted, fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, marginTop: 2 }}>{projectName(t.project_id)}</div>
            </div>
            <button onClick={() => unpin(t)} title="Send back — keeps the task, just off this list"
              style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 7, padding: '5px 9px', cursor: 'pointer', color: B.muted, fontSize: 11, fontWeight: 700, fontFamily: FONT }}>
              ↩
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
